import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, MagnifyingGlass, UserCircle } from "@phosphor-icons/react";
import { api, fileUrl, trackPage } from "../lib/api";
import { useLang } from "../i18n";
import ScrollStrip from "../components/landing/ScrollStrip";

const CLASS_ORDER = [
    "Tim Hijau (Pemula)",
    "Tim Kuning",
    "Tim Jingga",
    "Tim Biru",
    "Tim Pink",
    "Tim Merah",
    "Speed",
    "Slalom",
];

const KU_OPTIONS = ["KU A", "KU B", "KU C", "KU D", "Junior", "Senior"];

export default function AtletList() {
    const { t } = useLang();
    const [athletes, setAthletes] = useState(null);
    const [search, setSearch] = useState("");
    const [kuFilter, setKuFilter] = useState("");

    useEffect(() => {
        window.scrollTo(0, 0);
        trackPage("/atlet");
        api.get("/content")
            .then((r) => setAthletes((r.data.athletes || []).filter((a) => (a.status ?? "active") !== "inactive")))
            .catch(() => setAthletes([]));
    }, []);

    const visibleAthletes = useMemo(() => {
        if (!athletes) return [];
        const q = search.trim().toLowerCase();
        return athletes.filter((a) =>
            (!q || (a.name || "").toLowerCase().includes(q)) &&
            (!kuFilter || a.ku === kuFilter)
        );
    }, [athletes, search, kuFilter]);

    if (athletes === null) return <div className="min-h-screen bg-white" data-testid="atlet-list-loading" />;

    const groupMap = {};
    const groups = [];
    visibleAthletes.forEach((a) => {
        const key = (a.class_name || "").trim();
        if (!groupMap[key]) {
            groupMap[key] = { name: key, items: [] };
            groups.push(groupMap[key]);
        }
        groupMap[key].items.push(a);
    });
    groups.sort((x, y) => {
        const xi = CLASS_ORDER.indexOf(x.name);
        const yi = CLASS_ORDER.indexOf(y.name);
        return (xi === -1 ? 99 : xi) - (yi === -1 ? 99 : yi);
    });

    return (
        <div className="min-h-screen bg-white text-neutral-900" data-testid="atlet-list-page">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
                    <Link
                        to="/"
                        data-testid="atlet-list-back"
                        className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-racing-muted hover:text-neutral-900 transition-colors duration-300"
                    >
                        <ArrowLeft size={16} weight="bold" /> {t.back}
                    </Link>

                    <h1 className="mt-8 font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-none" data-testid="atlet-list-title">
                        {t.ch_atlet_t}
                    </h1>
                    <p className="mt-4 text-sm font-light text-racing-muted">
                        {visibleAthletes.length} {t.rank_athlete}
                    </p>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
                        <div className="relative flex-1">
                            <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-racing-muted" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Ketik sebagian nama atlet..."
                                data-testid="atlet-list-search-input"
                                className="w-full bg-white border border-racing-line pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                            />
                        </div>
                        <select
                            value={kuFilter}
                            onChange={(e) => setKuFilter(e.target.value)}
                            data-testid="atlet-list-ku-filter"
                            className="bg-white border border-racing-line px-4 py-3.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                        >
                            <option value="">Semua KU</option>
                            {KU_OPTIONS.map((ku) => <option key={ku} value={ku}>{ku}</option>)}
                        </select>
                    </div>

                    {visibleAthletes.length === 0 && (
                        <p className="mt-10 border border-racing-line bg-racing-surface p-6 text-sm text-racing-muted" data-testid="atlet-list-search-empty">
                            Tidak ada atlet yang cocok{search ? ` dengan "${search}"` : ""}{kuFilter ? ` pada ${kuFilter}` : ""}.
                        </p>
                    )}

                    <div className="mt-12 space-y-12">
                        {groups.map((group, gi) => (
                            <div key={group.name || "other"} data-testid={`atlet-page-class-group-${gi}`}>
                                <div className="flex items-baseline gap-4 mb-5">
                                    <h2 className="font-display uppercase text-2xl md:text-3xl text-neutral-900" data-testid={`atlet-page-class-title-${gi}`}>
                                        {group.name || t.atlet_class_other}
                                    </h2>
                                    <span className="text-[10px] uppercase tracking-[0.25em] text-racing-golddeep">
                                        {group.items.length} {t.rank_athlete}
                                    </span>
                                </div>
                                <ScrollStrip testid={gi === 0 ? "atlet-page-strip" : `atlet-page-strip-${gi}`} gapClass="gap-5">
                                    {group.items.map((a, i) => (
                                        <Link
                                            key={a.id}
                                            to={`/atlet/${a.id}`}
                                            data-testid={`atlet-list-card-${gi}-${i}`}
                                            className="group relative block w-28 md:w-32 shrink-0 snap-start overflow-hidden border border-racing-line bg-racing-surface"
                                        >
                                            <div className="relative aspect-[3/4] overflow-hidden">
                                                {a.photo_url ? (
                                                    <img
                                                        src={fileUrl(a.photo_url)}
                                                        alt={a.name}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-[#F1ECE3]">
                                                        <UserCircle size={40} weight="duotone" className="text-neutral-900/10" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-2.5 flex items-center justify-between gap-2">
                                                <div className="min-w-0">
                                                    <h3 className="font-display uppercase text-sm text-neutral-900 group-hover:text-racing-red transition-colors duration-300 truncate">
                                                        {a.name}
                                                    </h3>
                                                    {a.ku && <p className="text-[9px] uppercase tracking-[0.15em] text-racing-golddeep truncate" data-testid={`atlet-ku-${i}`}>{a.ku}</p>}
                                                </div>
                                                <ArrowUpRight size={12} weight="bold" className="shrink-0 text-racing-red transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                                            </div>
                                        </Link>
                                    ))}
                                </ScrollStrip>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
